import { deleteObject, getStorage, ref } from "firebase/storage";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { v4 } from "uuid";
import { GenericSvg } from "../../assets/svgs/GenericSVG";
import { IconButton } from "../../core/components/buttons/IconButton";
import { useEventIndicator } from "../../core/components/eventIndicator/useEventIndicator";
import { MediaUpload } from "../../core/components/input/MediaUpload";
import { InfoText } from "../../core/components/texts/InfoText";
import {
  getAppointmentPracticeId,
  getAppointmentRecipeAssets,
} from "../../core/store/appointmentSlice";
import { useMainStore } from "../../core/store/mainStore";
import { useStyles } from "../../core/styles/theme";
import { useHandleAppointmentBookingNavigation } from "../navigation/utils/useHandleAppointmentBookingNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import { uploadDocumentImage } from "../onboarding/utils/uploadDocumentImage";

const sanitizeFileName = (name: string) =>
  name.replace(/[^\w.-]+/g, "_").slice(-80);

export const AppointmentRecipeUploadScreen = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useHandleAppointmentBookingNavigation();
  const theme = useStyles();
  const eventIndicator = useEventIndicator();

  // zustand
  const appointmentRecipeAssets = useMainStore(getAppointmentRecipeAssets);
  const practiceId = useMainStore(getAppointmentPracticeId);

  const setAppointmentRecipeAssets = useMainStore(
    (state) => state.setAppointmentRecipeAssets,
  );

  const handleNewFiles = (files: FileList) => {
    const newAssets = Array.from(files).map((file) => ({
      id: v4(),
      file: file,
    }));

    const updatedAssets = [
      ...(newAssets ?? []).map((asset) => ({
        assetUrl: URL.createObjectURL(asset.file),
        file: asset.file,
        type: "image",
        mimeType: asset.file.type,
        id: asset.id,
        internalName: asset.file.name,
      })),
      ...(appointmentRecipeAssets ?? []),
    ];
    setAppointmentRecipeAssets(
      updatedAssets as {
        assetUrl: string;
        file?: File;
        type: "image";
        mimeType?: string;
        internalName: string;
      }[],
    );
  };

  const handleRemoveAsset = async (asset: {
    assetUrl: string;
    file?: File;
    type: string;
    mimeType?: string;
    internalName: string;
  }) => {
    const isLocal = Boolean(asset.file);
    if (isLocal) {
      const updatedAssets = appointmentRecipeAssets?.filter(
        (a) => a.assetUrl !== asset.assetUrl,
      );
      setAppointmentRecipeAssets(updatedAssets);
    } else {
      try {
        const storage = getStorage();
        const storageRef = ref(storage, asset.assetUrl);
        await deleteObject(storageRef);
        const updatedAssets = appointmentRecipeAssets?.filter(
          (a) => a.assetUrl !== asset.assetUrl,
        );
        setAppointmentRecipeAssets(updatedAssets);
      } catch (error) {
        console.error("Error removing asset:", error);
      }
    }
  };

  const handleUploadAssets = async () => {
    const assetsToUpload = appointmentRecipeAssets?.filter((asset) =>
      Boolean(asset.file),
    );
    const patientFormId = v4();

    if (assetsToUpload && assetsToUpload.length > 0 && practiceId) {
      try {
        setLoading(true);
        const uploadedAssets: {
          assetUrl: string;
          type: "image";
          mimeType?: string;
          internalName: string;
        }[] = [];
        for (const asset of assetsToUpload) {
          // patientFormId is fresh per run, but two files inside one run still collide on the raw
          // name: iOS Safari names every camera capture image.jpg (PF-5488).
          const bucketPath = `patientForms/${practiceId}/${patientFormId}/documents/${v4()}-${sanitizeFileName(asset.internalName)}`;
          const uploadedAsset = await uploadDocumentImage(
            asset.file as File,
            bucketPath,
          );
          uploadedAssets.push(uploadedAsset);
        }
        const allAssets = [
          ...(appointmentRecipeAssets?.filter((asset) => !asset.file) || []),
          ...(uploadedAssets || []),
        ];
        return allAssets;
      } catch (error) {
        console.error("Error uploading recipe assets:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    } else {
      return appointmentRecipeAssets;
    }
  };

  const handleNext = async () => {
    try {
      setLoading(true);
      eventIndicator("info", t("UPLOADING_DOCUMENTS"));
      const uploadedAssets = await handleUploadAssets();
      setAppointmentRecipeAssets(uploadedAssets);
      eventIndicator("success", t("DOCUMENTS_UPLOADED"));
      navigate();
    } catch (error) {
      console.error("Error during asset upload:", error);
      eventIndicator("error", t("ERROR_UPLOADING_DOCUMENTS"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppointmentScreenWrapper
      onNext={handleNext}
      nextButtonLoading={loading}
      nextButtonDisabled={
        appointmentRecipeAssets?.length === 0 || !appointmentRecipeAssets
      }
    >
      <InfoText
        headline={t("APPOINTMENT_RECIPE_HEADLINE")}
        text={[t("APPOINTMENT_RECIPE_DESCRIPTION")]}
      />
      <Container>
        <MediaUpload
          canUploadMultipleFiles
          handleUpload={handleNewFiles}
          type={"file"}
          placeHolderIcon="DocumentUpload"
        />
        <ImagesPreview>
          {appointmentRecipeAssets?.map((asset) => (
            <ImagePreviewContainer
              key={asset?.assetUrl}
              $amountOfPreviews={Math.max(
                4,
                Math.min(appointmentRecipeAssets.length, 6),
              )}
            >
              {asset.mimeType?.includes("jpg") ||
              asset.mimeType?.includes("jpeg") ||
              asset.mimeType?.includes("png") ||
              asset.mimeType?.includes("gif") ? (
                <ImagePreview
                  src={asset.assetUrl}
                  onClick={() => handleRemoveAsset(asset)}
                />
              ) : (
                <GenericSvg
                  name="Document"
                  onClick={() => handleRemoveAsset(asset)}
                  size={40}
                />
              )}
              <CloseButton
                iconName="Close"
                onClick={() => handleRemoveAsset(asset)}
                primaryColor={theme.colors.white}
                size={20}
              />
            </ImagePreviewContainer>
          ))}
        </ImagesPreview>
      </Container>
    </AppointmentScreenWrapper>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ImagesPreview = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const ImagePreview = styled.img`
  border-radius: 10px;
  overflow: hidden;
  object-fit: contain;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
  display: none;
`;

const ImagePreviewContainer = styled.div<{
  $amountOfPreviews: number;
}>`
  height: 100px;
  width: calc((100% / ${({ $amountOfPreviews }) => $amountOfPreviews}) - 10px);
  max-width: calc(
    (100% / ${({ $amountOfPreviews }) => $amountOfPreviews}) - 10px
  );
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.light};

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    ${CloseButton} {
      position: absolute;
      top: 0;
      right: 0;
      display: inherit;
    }
  }
`;
